import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase, Post } from '@/lib/supabase';
import PostList from './components/PostList';
import PostEditor from './components/PostEditor';

type View = 'list' | 'editor';

const ADMIN_PASSWORD = 'grainn2026';

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  const [view, setView] = useState<View>('list');
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPost, setEditingPost] = useState<Partial<Post> | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('posts')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      showNotification('error', '記事の取得に失敗しました');
    } else {
      setPosts(data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (authed) fetchPosts();
  }, [authed, fetchPosts]);

  const handleLogin = () => {
    if (passwordInput === ADMIN_PASSWORD) {
      setAuthed(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const handleSave = async (data: Partial<Post>) => {
    setSaving(true);
    try {
      if (editingPost?.id) {
        const { error } = await supabase
          .from('posts')
          .update({ ...data, updated_at: new Date().toISOString() })
          .eq('id', editingPost.id);
        if (error) throw error;
        showNotification('success', '記事を更新しました');
      } else {
        const { error } = await supabase
          .from('posts')
          .insert([{ ...data, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }]);
        if (error) throw error;
        showNotification('success', '記事を作成しました');
      }
      await fetchPosts();
      setView('list');
      setEditingPost(null);
    } catch {
      showNotification('error', '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('posts').delete().eq('id', id);
    if (error) {
      showNotification('error', '削除に失敗しました');
    } else {
      showNotification('success', '記事を削除しました');
      await fetchPosts();
    }
    setDeleteConfirm(null);
  };

  const handleTogglePublish = async (post: Post) => {
    const { error } = await supabase
      .from('posts')
      .update({ published: !post.published, updated_at: new Date().toISOString() })
      .eq('id', post.id);
    if (error) {
      showNotification('error', '更新に失敗しました');
    } else {
      showNotification('success', post.published ? '非公開にしました' : '公開しました');
      await fetchPosts();
    }
  };

  const filteredPosts = posts.filter((p) => {
    const matchStatus =
      filterStatus === 'all' ||
      (filterStatus === 'published' && p.published) ||
      (filterStatus === 'draft' && !p.published);
    const matchSearch =
      !searchQuery ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchSearch;
  });

  // Login screen
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F8FAFF] flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <span className="text-2xl font-black tracking-widest text-[#0A1628]">GRAINN</span>
            </Link>
            <p className="text-[#999] text-sm mt-2">管理画面</p>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-[#E8F0FF]">
            <h1 className="text-lg font-bold text-[#0A1628] mb-6">ログイン</h1>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#666] mb-1.5">パスワード</label>
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                  placeholder="パスワードを入力"
                  className={`w-full text-sm border rounded-xl px-4 py-3 focus:outline-none transition-colors ${passwordError ? 'border-red-300 focus:border-red-400' : 'border-[#E8F0FF] focus:border-[#1E6FFF]'}`}
                />
                {passwordError && <p className="text-xs text-red-500 mt-1">パスワードが正しくありません</p>}
              </div>
              <button
                onClick={handleLogin}
                className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
                style={{ background: 'linear-gradient(90deg, #1E6FFF, #0099FF)' }}
              >
                ログイン
              </button>
            </div>
          </div>
          <p className="text-center text-xs text-[#bbb] mt-6">
            <Link to="/" className="hover:text-[#1E6FFF] transition-colors">← サイトに戻る</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFF] flex flex-col">
      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl text-sm font-medium text-white transition-all ${
            notification.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'
          }`}
        >
          <i className={`ri-${notification.type === 'success' ? 'check' : 'error-warning'}-line mr-2`} />
          {notification.message}
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 border border-[#E8F0FF]">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="text-base font-bold text-[#0A1628] text-center mb-2">記事を削除しますか？</h3>
            <p className="text-xs text-[#999] text-center mb-6">この操作は取り消せません。</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-[#F5F7FA] text-[#666] hover:bg-[#E8F0FF] transition-all cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-500 hover:bg-red-600 transition-all cursor-pointer whitespace-nowrap"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}

      {view === 'editor' ? (
        <div className="flex-1 flex flex-col bg-white">
          <PostEditor
            post={editingPost}
            onSave={handleSave}
            onCancel={() => { setView('list'); setEditingPost(null); }}
            saving={saving}
          />
        </div>
      ) : (
        <>
          {/* Top Bar */}
          <header className="bg-white border-b border-[#E8F0FF] px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-lg font-black tracking-widest text-[#0A1628]">GRAINN</Link>
              <span className="text-[#ccc]">/</span>
              <span className="text-sm font-semibold text-[#666]">ブログ管理</span>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/blog"
                target="_blank"
                className="text-xs text-[#666] hover:text-[#1E6FFF] transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                <i className="ri-external-link-line" />
                ブログを見る
              </Link>
              <button
                onClick={() => { setEditingPost(null); setView('editor'); }}
                className="flex items-center gap-2 text-xs px-5 py-2.5 rounded-xl font-bold text-white transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
                style={{ background: 'linear-gradient(90deg, #1E6FFF, #0099FF)' }}
              >
                <i className="ri-add-line" />
                新規作成
              </button>
            </div>
          </header>

          {/* Main */}
          <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-8">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { label: '総記事数', value: posts.length, icon: 'ri-article-line', color: '#1E6FFF' },
                { label: '公開中', value: posts.filter((p) => p.published).length, icon: 'ri-eye-line', color: '#00B478' },
                { label: '下書き', value: posts.filter((p) => !p.published).length, icon: 'ri-draft-line', color: '#FF8C00' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-2xl p-5 border border-[#E8F0FF]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: `${stat.color}15` }}>
                      <i className={`${stat.icon} text-lg`} style={{ color: stat.color }} />
                    </div>
                    <div>
                      <p className="text-2xl font-black text-[#0A1628]">{stat.value}</p>
                      <p className="text-xs text-[#999]">{stat.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Filter & Search */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <div className="flex items-center gap-1 bg-white border border-[#E8F0FF] rounded-xl p-1">
                {(['all', 'published', 'draft'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    className={`text-xs px-4 py-1.5 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                      filterStatus === s ? 'text-white' : 'text-[#666] hover:text-[#1E6FFF]'
                    }`}
                    style={filterStatus === s ? { background: 'linear-gradient(90deg, #1E6FFF, #0099FF)' } : {}}
                  >
                    {s === 'all' ? 'すべて' : s === 'published' ? '公開中' : '下書き'}
                  </button>
                ))}
              </div>
              <div className="flex-1 min-w-[200px] relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#bbb] text-sm" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="記事を検索..."
                  className="w-full text-sm border border-[#E8F0FF] rounded-xl pl-9 pr-4 py-2.5 focus:outline-none focus:border-[#1E6FFF] bg-white"
                />
              </div>
            </div>

            {/* List */}
            <div className="bg-white rounded-2xl border border-[#E8F0FF] p-4">
              {loading ? (
                <div className="text-center py-16">
                  <div className="w-8 h-8 border-2 border-[#1E6FFF] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-xs text-[#999]">読み込み中...</p>
                </div>
              ) : (
                <PostList
                  posts={filteredPosts}
                  onEdit={(post) => { setEditingPost(post); setView('editor'); }}
                  onDelete={(id) => setDeleteConfirm(id)}
                  onTogglePublish={handleTogglePublish}
                />
              )}
            </div>
          </main>
        </>
      )}
    </div>
  );
}
